<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useDemoChat, type Message } from '@/composables/useDemoChat';
import DemoChatPanel from './DemoChatPanel.vue';

type LlmStyle = 'chatgpt' | 'claude' | 'claude-code';

const props = withDefaults(defineProps<{
  llmStyle?: LlmStyle;
}>(), {
  llmStyle: 'chatgpt',
});

interface Flashcard {
  question: string;
  answer: string;
  highlighted?: boolean;
}

// Demo script
const conversation: Message[] = [
  { role: 'user', content: "I've been having trouble with the flashcard that tests me on who discovered the forgetting curve and when. Can you edit it to only test who discovered it?" },
  { role: 'assistant', content: "Sure! I'll simplify that card to focus on just who discovered it." },
];

const initialCards: Flashcard[] = [
  { question: 'Who discovered the forgetting curve and when?', answer: 'Hermann Ebbinghaus in 1885', highlighted: true },
  { question: 'What is the testing effect?', answer: 'Actively recalling strengthens memory more than re-reading' },
  { question: 'What is the optimal retention rate?', answer: '85-90%' },
];

const editedCards: Flashcard[] = [
  { question: 'Who discovered the forgetting curve?', answer: 'Hermann Ebbinghaus', highlighted: true },
  { question: 'What is the testing effect?', answer: 'Actively recalling strengthens memory more than re-reading' },
  { question: 'What is the optimal retention rate?', answer: '85-90%' },
];

const {
  visibleMessages, isTyping, typingContent, typingRole, demoVisible,
  sleep, showUserMessage, showAssistantMessage, reset, fadeIn, cleanup,
  MESSAGE_DELAY,
} = useDemoChat();

// Demo-specific state
const currentCards = ref<Flashcard[]>([...initialCards]);
const isEditing = ref(false);
const EDIT_DELAY = 600;

async function runDemo() {
  reset();
  currentCards.value = [...initialCards];
  isEditing.value = false;

  await fadeIn();

  await showUserMessage(conversation[0]);
  await sleep(MESSAGE_DELAY);
  await showAssistantMessage(conversation[1]);

  // Show editing state then update card
  await sleep(EDIT_DELAY);
  isEditing.value = true;
  await sleep(1000);
  currentCards.value = [...editedCards];
  isEditing.value = false;
}

onMounted(() => runDemo());
onUnmounted(() => cleanup());
</script>

<template>
  <Transition name="demo">
    <div v-show="demoVisible" class="grid md:grid-cols-2 gap-6">
    <!-- Left: Chat -->
    <DemoChatPanel
      :llm-style="props.llmStyle"
      :visible-messages="visibleMessages"
      :is-typing="isTyping"
      :typing-content="typingContent"
      :typing-role="typingRole"
    />

    <!-- Right: App -->
    <div>
      <h2 class="text-xl font-semibold text-foreground mb-3">Your flashcards in LLM-SR</h2>
      <div class="rounded-lg border border-border bg-card overflow-hidden shadow-md">
      <!-- Header bar -->
      <div class="px-3 py-2 border-b border-border bg-muted/50 flex items-center gap-2">
        <div class="flex gap-1.5">
          <div class="w-2.5 h-2.5 rounded-full bg-red-400/70"></div>
          <div class="w-2.5 h-2.5 rounded-full bg-yellow-400/70"></div>
          <div class="w-2.5 h-2.5 rounded-full bg-green-400/70"></div>
        </div>
      </div>

      <!-- App content -->
      <div class="p-4 h-[220px] md:h-[320px] overflow-hidden">
        <div class="flex items-center justify-between mb-3">
          <h3 class="font-medium text-foreground text-sm">Spaced Repetition</h3>
          <span class="text-xs text-muted-foreground">3 cards</span>
        </div>

        <div class="space-y-2">
          <div
            v-for="card in currentCards"
            :key="card.question"
            class="px-3 py-2 rounded-md border transition-all duration-300"
            :class="
              card.highlighted
                ? isEditing
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-400'
                  : 'bg-primary/10 border-primary/30'
                : 'bg-muted/50 border-border'
            "
          >
            <p class="text-sm text-foreground">{{ card.question }}</p>
            <p class="text-xs text-muted-foreground mt-1">{{ card.answer }}</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
  </Transition>
</template>

<style scoped>
/* Message fade-in (skip for typed messages) */
.message-enter-active:not(.skip-transition) {
  transition: opacity 0.3s ease;
}
.message-enter-from:not(.skip-transition) {
  opacity: 0;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Demo fade in/out */
.demo-enter-active,
.demo-leave-active {
  transition: opacity 0.4s ease;
}
.demo-enter-from,
.demo-leave-to {
  opacity: 0;
}
</style>
